import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Briefcase, Building2, Code2, Landmark } from 'lucide-react';

/**
 * Three doors, then employers.
 *
 * The overview deck names the ways in: the organizations that deliver care,
 * the funders that pay for it, and the partners that build on it. Each door
 * says who it is for in the reader's own words before it says anything about
 * the product. Employers are a different buyer, so they get their own, smaller
 * door rather than a column beside a community organization.
 */
const DOORS = [
  {
    icon: Building2,
    who: 'I deliver care',
    for: 'Community organizations, hubs, CHW and promotora programs',
    promise: 'Capture the visit once and get paid by every funder it qualifies for — plans, counties and grants — and find the funders you are not billing yet.',
    to: '/solutions/hubs',
    cta: 'For community organizations',
  },
  {
    icon: Landmark,
    who: 'I fund care',
    for: 'Health plans, counties and state programs',
    promise: 'Know which organization delivered which service to which member, on what date — as a record, not an estimate.',
    to: '/solutions/health-plans',
    cta: 'For health plans & counties',
  },
  {
    icon: Code2,
    who: 'I build products',
    for: 'Platforms that already serve the population',
    promise: 'Every funder’s interface behind one API and an embedded agent: eligibility, submission, disbursement and remittance.',
    to: '/partners',
    cta: 'For partners & developers',
  },
];

const AudienceDoors: React.FC = () => (
  <section className="py-16 bg-gray-50 dark:bg-gray-900 border-y border-gray-200 dark:border-gray-800">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="text-2xl md:text-3xl font-bold text-ink mb-10 text-center">
        One ledger, three ways in
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {DOORS.map((d) => (
          <Link
            key={d.to}
            to={d.to}
            className="group rounded-2xl bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 p-7 flex flex-col hover:border-v2accent hover:shadow-lg transition-all"
          >
            <d.icon className="w-8 h-8 text-v2accent mb-4" aria-hidden="true" />
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{d.who}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{d.for}</p>
            <p className="text-gray-700 dark:text-gray-300 flex-1">{d.promise}</p>
            <span className="mt-6 inline-flex items-center font-semibold text-ink">
              {d.cta}
              <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
            </span>
          </Link>
        ))}
      </div>
      <Link
        to="/solutions/employers"
        className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:text-ink"
      >
        <Briefcase className="w-4 h-4" aria-hidden="true" />
        Self-insured employer? See employee health &amp; SDOH
        <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </section>
);

export default AudienceDoors;
